import React, { useState, useEffect, useMemo } from "react";
import { connect } from "react-redux";
import { withTranslation } from "react-i18next";
import { getFormSyncErrors, getFormValues, isDirty } from "redux-form";
import DynamicForm from "../DynamicForm";
import Loader from "../Loader";
import permissions, { checkPermissions } from "config/permissions";
import cx from "classnames";
import { isEmpty } from "lodash";

import { getDynamicFormSturcture } from "./helpers";
import { configureFilterObject } from "../util/configureFilterObject";

import {
    closeFilterForm,
    setAttributes,
    openFilterParams,
    setFilters,
    setFiltersLoading,
} from "reducers/attributesFilters";

import "./styles.scss";
import BaseService from "../../../../services/BaseService";

const FORM_HEADER_NAME = (storeKey, extraFormKey) =>
    extraFormKey ? `${storeKey}-${extraFormKey}-filters-header` : `${storeKey}-filters-header`;
const FORM_MAIN_NAME = (storeKey, extraFormKey) =>
    extraFormKey ? `${storeKey}-${extraFormKey}-filters-main` : `${storeKey}-filters-main`;

const mapStateToProps = (state, ownProps) => {
    const attributesFilters = state.attributesFilters[ownProps.storeKey];
    return {
        attributes: attributesFilters && attributesFilters.attributes,
        filters: attributesFilters && attributesFilters.filters,
        isFiltersLoading: attributesFilters && attributesFilters.isFiltersLoading,

        isCanCreatePublicFilter: checkPermissions(permissions.FilterOperations.create_public_filter),
        formHeaderValues: getFormValues(FORM_HEADER_NAME(ownProps.storeKey, ownProps.extraFormKey))(state),
        formHeaderErrors: getFormSyncErrors(FORM_HEADER_NAME(ownProps.storeKey, ownProps.extraFormKey))(state),
        formMainValues: getFormValues(FORM_MAIN_NAME(ownProps.storeKey, ownProps.extraFormKey))(state),
        formMainErrors: getFormSyncErrors(FORM_MAIN_NAME(ownProps.storeKey, ownProps.extraFormKey))(state),
        isFormHeaderDirty: isDirty(FORM_HEADER_NAME(ownProps.storeKey, ownProps.extraFormKey))(state),
        isFormMainDirty: isDirty(FORM_MAIN_NAME(ownProps.storeKey, ownProps.extraFormKey))(state),
    };
};

const mapDispatchToProps = {
    closeFilterForm,
    setAttributes,
    openFilterParams,
    setFilters,
    setFiltersLoading,
};

const SidebarFiltersForm = props => {
    const {
        t,
        isCanCreatePublicFilter,
        storeKey,
        closeFilterForm,
        activeFilter,
        isNewFilter,
        isEditFilter,
        isFilterParams,
        filters,
        attributes,
        setAttributes,
        attributesRequestKey,
        attributesRequestParams = {},
        attributesValuesRequestKey,
        attributesValuesRequestParams = {},
        formHeaderValues,
        formHeaderErrors,
        formMainValues,
        formMainErrors,
        onFilterSave,
        openFilterParams,
        isFiltersLoading,
        isFormHeaderDirty,
        isFormMainDirty,
        withLocalFiltersList,
        withoutSave,
        setFilters,
        setFiltersLoading,
        filtersCode,
        isKeepOnUnmount,
        extraFormKey,
    } = props;

    if (!storeKey) {
        console.error("SidebarFiltersForm:: No {storeKey} is provided!");
    }

    // const [attributes, setAttributes] = useState(null);
    const [attributesValues, setAttributesValues] = useState([]);
    const [dynParams, setDynParams] = useState(null);
    const [isAttributesLoading, setIsAttributesLoading] = useState(false);
    const [isValuesLoading, setIsValuesLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isMounted, setIsMounted] = useState(false);

    const [forceHeaderInitialValues, setForceHeaderInitialValues] = useState(null);
    const [forceMainInitialValues, setForceMainInitialValues] = useState(null);
    const [isFormValuesUpdateRequired, setIsFormValuesUpdateRequired] = useState(false);

    const [headerFormTimestamp, setHeaderFormTimestamp] = useState(null); // used to remount header form in case there are no withLocalFiltersList prop
    const [dynParamsTimestamp, setDynParamsTimestamp] = useState(null);

    const isLoading = isAttributesLoading || isValuesLoading || isSaving;

    const activeFilterId = useMemo(() => activeFilter && activeFilter.filterId, [activeFilter]);

    const isNoErrors = isEmpty(formHeaderErrors) && isEmpty(formMainErrors);

    const isFormDirty = isEditFilter ? isFormHeaderDirty || isFormMainDirty : isFormMainDirty;

    const useLocalFilter = useMemo(() => {
        return formHeaderValues && formHeaderValues.useFilter;
    }, [formHeaderValues]);

    const formRerenderKey = withLocalFiltersList
        ? useLocalFilter
        : `${activeFilter && activeFilter.filterId}_${activeFilter &&
              activeFilter.filterName}_${isNewFilter}_${isFilterParams}_${isEditFilter}`;

    const initHeaderForm = useMemo(() => {
        if (withLocalFiltersList) {
            const values = !filters ? [] : filters.map(filter => ({ value: filter.filterName, key: filter.filterId }));
            return [
                {
                    view: "1column",
                    collapsed: false,
                    widgets: [
                        {
                            key: "useFilter",
                            title: t("filters.useFilter"),
                            values,
                            widgetType: "select",
                        },
                    ],
                },
            ];
        }
        const widgets = [
            {
                key: "filterName",
                title: t("filterName"),
                clearable: true,
                isReq: true,
                maxLength: 200,
                disabled: isFilterParams,
                savedValue: isNewFilter ? `Фiльтр ${filters.length + 2}` : activeFilter.filterName,
                widgetType: "input",
            },
        ];
        if (isCanCreatePublicFilter) {
            widgets.push({
                key: "isPublic",
                title: t("filterIsPublic"),
                clearable: true,
                savedValue: isNewFilter ? false : activeFilter.predefined || !activeFilter.custom, // set isPublic if activeFilter is either predefined or not custom
                disabled: isFilterParams,
                widgetType: "checkboxToggle",
            });
        }
        return [
            {
                view: "1column",
                collapsed: false,
                key: "static-issue",
                widgets,
            },
        ];
    }, [filters, withLocalFiltersList, activeFilter, isNewFilter, isFilterParams, isCanCreatePublicFilter]);

    function handleFilterSave() {
        const filterObject = configureFilterObject(formMainValues, attributes);
        const data = {
            query: "",
            filterObject,
            name: formHeaderValues.filterName,
            isPublic: Boolean(formHeaderValues.isPublic).toString(),
        };

        if (isEditFilter) {
            data.filterSetId = activeFilter.filterId;
        }

        const onSuccess = () => {
            setIsSaving(false);
            if (isEditFilter) {
                openFilterParams(storeKey);
            }
        };
        const onFailure = () => {
            setIsSaving(false);
        };
        setIsSaving(true);
        onFilterSave(data, onSuccess, onFailure);
    }

    async function getFiltersAttributes() {
        try {
            setIsAttributesLoading(true);
            const params = { data: { ...attributesRequestParams } };

            const { result, success } = await BaseService.get(attributesRequestKey, params);
            if (success) {
                setIsAttributesLoading(false);
                setAttributes(result, storeKey);
            }
        } catch (e) {
            setIsAttributesLoading(false);
            console.error("AttributesFiltersForm::getFiltersAttributes: Error", e);
        }
    }

    async function getFilterValues(filterId) {
        try {
            const params = { data: { id: filterId, ...attributesValuesRequestParams } };
            setIsValuesLoading(true);
            const { result, success } = await BaseService.get(attributesValuesRequestKey, params);
            if (success) {
                setAttributesValues(result);
                setIsValuesLoading(false);
                setIsFormValuesUpdateRequired(true);
            }
        } catch (e) {
            setIsValuesLoading(false);
            console.error("AttributesFiltersForm::getFilterValues: Error", e);
        }
    }

    async function getFiltersList() {
        try {
            setFiltersLoading(true, storeKey);
            const { result, success } = await BaseService.get("filters", { data: { code: filtersCode } });
            if (success) {
                // setIsLoading(false);
                setFiltersLoading(false, storeKey);
                const allFilters = result.map(filter => ({
                    ...filter,
                    filterId: filter.value,
                    filterName: filter.name,
                    custom: filter.custom,
                    predefined: filter.predefined,
                    page: 1,
                }));
                setFilters(allFilters, storeKey);
            }
        } catch (e) {
            setFiltersLoading(false, storeKey);
            console.error("AttributesFiltersForm::getFiltersList Error during filters request: ", e);
        }
    }

    useEffect(() => {
        if (attributes && attributesValues) {
            const dynFormStructure = getDynamicFormSturcture(attributes, attributesValues, isFilterParams);
            setDynParams(dynFormStructure);
        }
    }, [attributes, attributesValues, isFilterParams, useLocalFilter]);

    useEffect(() => {
        if (activeFilter) {
            setDynParams(null);
            // get values if that is not isNewFilter, othervise set values as an empty array
            if (!isNewFilter) {
                getFilterValues(activeFilterId);
            } else {
                setAttributesValues([]);
            }
        }
    }, [activeFilter, isNewFilter]);

    useEffect(() => {
        // if (dynParams) {
        //     setDynParams(null);
        // }
        if (isMounted) {
            if (useLocalFilter) {
                getFilterValues(useLocalFilter);
            } else {
                setAttributesValues([]);
            }
        }
    }, [useLocalFilter]);

    // local filter is changed => update form values
    useEffect(() => {
        if (isKeepOnUnmount && dynParams && isFormValuesUpdateRequired) {
            const initialValues = {};
            dynParams.forEach(block => {
                block.widgets.forEach(widget => {
                    initialValues[widget.key] = widget.savedValue || widget.savedValues;
                });
            });
            setDynParamsTimestamp(new Date().getTime());
            setForceMainInitialValues(initialValues);
        }
    }, [dynParams, isKeepOnUnmount, isFormValuesUpdateRequired]);

    useEffect(() => {
        // only get attributes (form fields) in case they were not initialised before
        if (!attributes) {
            getFiltersAttributes();
        }
        // get filters in case it is used without SidebarFilters (search case)
        if (!filters && withLocalFiltersList) {
            getFiltersList();
            setAttributesValues([]);
        }
        setIsMounted(true);
    }, []);

    // initHeaderForm was changed => update key to rerender the form to keep savedValue sync
    useEffect(() => {
        if (!withLocalFiltersList) {
            setHeaderFormTimestamp(new Date().getTime());
        }
    }, [initHeaderForm]);

    return (
        <div className="attributes-filters-form">
            {isFiltersLoading && <Loader />}
            <div className={cx("attributes-filters-form__header", { withLocalFiltersList })}>
                {!isFiltersLoading && (
                    <DynamicForm
                        t={t}
                        forceInitialValues={forceHeaderInitialValues}
                        formName={FORM_HEADER_NAME(storeKey, extraFormKey)}
                        dynData={initHeaderForm}
                        destroyOnUnmount={!isKeepOnUnmount}
                        portalPlacement={"left"}
                        withoutLiveSaver
                        key={`${formRerenderKey}_${dynParamsTimestamp}_${headerFormTimestamp}`}
                    />
                )}
                {!withLocalFiltersList && (
                    <span className="attributes-filters-form__header-close" onClick={() => closeFilterForm(storeKey)}>
                        <i className="icon icon-close" />
                    </span>
                )}
            </div>
            <div
                className={cx("attributes-filters-form__fields", {
                    "without-bottom-border": withoutSave || isFilterParams,
                })}
            >
                {isLoading && <Loader />}
                {!isLoading && dynParams && (
                    <DynamicForm
                        t={t}
                        forceInitialValues={forceMainInitialValues}
                        formName={FORM_MAIN_NAME(storeKey, extraFormKey)}
                        dynData={dynParams}
                        destroyOnUnmount={!isKeepOnUnmount}
                        portalPlacement={"left"}
                        withoutLiveSaver
                        key={`${formRerenderKey}_${dynParamsTimestamp}`}
                    />
                )}
            </div>
            {!withoutSave && !isFilterParams && (
                <div className="attributes-filters-form__footer">
                    <button
                        className={cx("btn btn-primary", { disabled: isLoading || !isNoErrors || !isFormDirty })}
                        onClick={handleFilterSave}
                    >
                        {t("filters.save")}
                    </button>
                </div>
            )}
        </div>
    );
};

export default withTranslation()(connect(mapStateToProps, mapDispatchToProps)(SidebarFiltersForm));
